import { API, config } from '@/configs'
import { Loading } from '@/domains/appShell/loading'
import ViewErrorLoadingApp from '@/domains/appShell/loading/ErrorLoadingApp'
import { useLazyRestQuery } from '@/domains/appShell/services/useLazyRestQuery'
import { useLogout } from '@/domains/appShell/services/useLogout'
import { pendingRedirectAtom, sessionAtom } from '@/domains/appShell/stores/auth.store'
import { isMaintenanceModeAtom } from '@/domains/appShell/stores/maintenance.store'
import { userAtom } from '@/domains/appShell/stores/user.store'
import type { UserInfo } from '@/domains/appShell/types/user.type'
import { router } from '@/router'

import { lazy, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useIdleTimer } from 'react-idle-timer'
import { RouterProvider } from 'react-router-dom'

import * as Sentry from '@sentry/react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { RESET } from 'jotai/utils'

const ViewLogin = lazy(() => import('@/domains/appShell/login/Login'))

export default function Component() {
  const { i18n } = useTranslation()
  const { logout } = useLogout()

  const setUser = useSetAtom(userAtom)

  const session = useAtomValue(sessionAtom)
  const isMaintenanceMode = useAtomValue(isMaintenanceModeAtom)
  const [pendingRedirect, setPendingRedirect] = useAtom(pendingRedirectAtom)

  const {
    data: dataTranslate,
    fetch: fetchTranslate,
    isError: isTranslateError,
  } = useLazyRestQuery<Record<string, string>>(['translations'], API.translations.list, {
    scope: 'backoffice',
    locale: 'en',
  })

  const {
    data: dataUser,
    fetch: fetchUser,
    isError: isUserError,
  } = useLazyRestQuery<UserInfo>(['userinfo'], API.user.info)

  useIdleTimer({
    timeout: config.delayIdleTimer * 60_000,
    crossTab: true,
    onIdle: () => {
      if (session) {
        void logout()
      }
    },
    disabled: !session,
  })

  useEffect(() => {
    if (!session || dataUser || isMaintenanceMode) return
    void (async () => {
      const { data } = await fetchUser()
      if (!data) return
      setUser(data)

      // Set Sentry user context after successful login
      Sentry.setUser({
        id: data.id.toString(),
        email: data.email,
        username: `${data.firstName} ${data.lastName}`,
        provider: session.provider,
        role: data.role,
      })
    })()
  }, [session, fetchUser, dataUser, setUser, isMaintenanceMode])

  useEffect(() => {
    void (async () => {
      if (session && dataUser && dataTranslate) {
        if (pendingRedirect) {
          await router.navigate(pendingRedirect)
          setPendingRedirect(RESET)
        }
      }
    })()
  }, [session, dataUser, dataTranslate, pendingRedirect, setPendingRedirect])

  useEffect(() => {
    if (!session || dataTranslate || isMaintenanceMode) return
    void (async () => {
      const { data } = await fetchTranslate()
      if (!data) return
      i18n.addResourceBundle('en', 'translation', data, true, true)
    })()
  }, [session, fetchTranslate, dataTranslate, i18n, isMaintenanceMode])

  if (!session) {
    return <ViewLogin />
  }

  if ((isUserError || isTranslateError) && !isMaintenanceMode) {
    return <ViewErrorLoadingApp />
  }

  if (!dataUser || !dataTranslate || pendingRedirect) return <Loading />

  return <RouterProvider router={router} />
}
